import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaSeries,
  ColorType,
  createChart,
  LineType,
  type IChartApi,
  type ISeriesApi,
} from 'lightweight-charts'
import { useAuthStore } from '../auth/useAuthStore'
import { useActiveTrades } from '../hooks/useActiveTrades'
import { useMaskMoney } from '../privacy/usePrivacyStore'
import {
  getPortfolioSnapshots,
  type SnapshotRange,
} from '../api/portfolioHistory'
import { toLocalChartTime } from '../lib/chartTime'
import { useChartDragSelection } from '../lib/chartDragSelection'

const RANGES: SnapshotRange[] = ['1D', '1W', '1M', '3M', '1Y', 'ALL']

const REFETCH_MS: Record<SnapshotRange, number | false> = {
  '1D': 60_000,
  '1W': 5 * 60_000,
  '1M': false,
  '3M': false,
  '1Y': false,
  ALL: false,
}

const UP_LINE = '#10b981'
const UP_FILL_TOP = 'rgba(16,185,129,0.35)'
const DOWN_LINE = '#ef4444'
const DOWN_FILL_TOP = 'rgba(239,68,68,0.35)'
const GRAY_LINE = '#71717a'
const GRAY_FILL_TOP = 'rgba(113,113,122,0.12)'
const FILL_BOTTOM = 'rgba(0,0,0,0)'

function fmtMoneyRaw(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Real-epoch-ms formatter for tick labels along the x-axis.
function fmtTickLabel(ms: number, range: SnapshotRange): string {
  const d = new Date(ms)
  if (range === '1D') {
    const h24 = d.getHours()
    const m = d.getMinutes()
    const period = h24 >= 12 ? 'pm' : 'am'
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12
    return `${h12}:${m.toString().padStart(2, '0')}${period}`
  }
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

// Real-epoch-ms formatter for header labels; uses local-time fields.
function fmtHeaderDateTime(ms: number): string {
  const d = new Date(ms)
  const h24 = d.getHours()
  const m = d.getMinutes()
  const period = h24 >= 12 ? 'pm' : 'am'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${h12}:${m.toString().padStart(2, '0')}${period}`
}

export function PortfolioValueChart() {
  const accountId = useAuthStore((s) => s.accountId)
  const mask = useMaskMoney()
  const [range, setRange] = useState<SnapshotRange>('1D')

  const { data, isLoading } = useQuery({
    queryKey: ['portfolio-snapshots', accountId, range],
    queryFn: () => getPortfolioSnapshots(accountId!, range),
    enabled: !!accountId,
    refetchInterval: REFETCH_MS[range],
    staleTime: 30_000,
  })

  const points = data?.points ?? []

  // Mirror the /trades day-strip number: realized P/L for today as a % of
  // start-of-day equity. Hooks into the same dayStrip the /trades page renders,
  // so the two screens never disagree. Cached by react-query across components.
  const { dayStrip } = useActiveTrades()
  const todayPL = dayStrip[dayStrip.length - 1] ?? null
  const showRealizedPL =
    range === '1D' && todayPL && todayPL.tradeCount > 0

  const { first, last } = useMemo(() => {
    return {
      first: points[0]?.v ?? 0,
      last: points[points.length - 1]?.v ?? 0,
    }
  }, [points])

  // Build evenly-spaced tick labels across the data range. The built-in
  // lightweight-charts axis is hidden so we can control spacing exactly.
  const TICK_COUNT = 7
  const tickLabels = useMemo(() => {
    if (points.length < 2) return []
    const startMs = points[0].t
    const endMs = points[points.length - 1].t
    const span = endMs - startMs
    if (span <= 0) return []
    const labels: string[] = []
    for (let i = 0; i < TICK_COUNT; i++) {
      const ms = startMs + (span * i) / (TICK_COUNT - 1)
      labels.push(fmtTickLabel(ms, range))
    }
    return labels
  }, [points, range])

  const wrapRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null)
  const selSeriesRef = useRef<ISeriesApi<'Area'> | null>(null)

  const {
    hovered,
    selection,
    inSelection,
    selLeft,
    selRight,
    selDelta,
    selDeltaPct,
    selIsUp,
  } = useChartDragSelection({
    wrapRef,
    chartRef,
    seriesRef,
    resubscribeKey: points,
  })

  const selEndValue = selRight?.value ?? 0
  const selStartValue = selLeft?.value ?? 0

  const displayValue = inSelection ? selEndValue : (hovered?.value ?? last)
  const displayTime = inSelection
    ? selRight!.time
    : (hovered?.time ?? (points[points.length - 1]?.t ?? null))
  const delta = inSelection ? selDelta : displayValue - first
  const deltaPct = inSelection
    ? selDeltaPct
    : first > 0
      ? ((displayValue - first) / first) * 100
      : 0
  const isUp = inSelection ? selIsUp : delta >= 0

  // Range coloring keeps tracking the *whole-range* trend even while a selection is active
  // (so the chart fill doesn't recolor mid-drag).
  const rangeIsUp = last - first >= 0
  const lineColor = rangeIsUp ? UP_LINE : DOWN_LINE
  const fillTop = rangeIsUp ? UP_FILL_TOP : DOWN_FILL_TOP

  useEffect(() => {
    if (!containerRef.current) return
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#09090b' },
        textColor: '#a1a1aa',
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: { visible: false },
      leftPriceScale: { visible: false },
      timeScale: { visible: false },
      crosshair: { mode: 0 },
      handleScroll: false,
      handleScale: false,
    })
    const series = chart.addSeries(AreaSeries, {
      lineColor: UP_LINE,
      topColor: UP_FILL_TOP,
      bottomColor: FILL_BOTTOM,
      lineWidth: 2,
      lineType: LineType.Curved,
      priceLineVisible: false,
      lastValueVisible: false,
    })
    const selSeries = chart.addSeries(AreaSeries, {
      lineColor: UP_LINE,
      topColor: UP_FILL_TOP,
      bottomColor: FILL_BOTTOM,
      lineWidth: 2,
      lineType: LineType.Curved,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    })
    chartRef.current = chart
    seriesRef.current = series
    selSeriesRef.current = selSeries

    const el = containerRef.current
    chart.resize(el.clientWidth, el.clientHeight)
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) chart.resize(width, height)
    })
    ro.observe(el)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      selSeriesRef.current = null
    }
  }, [])

  useEffect(() => {
    const base = seriesRef.current
    const sel = selSeriesRef.current
    if (!base || !sel) return
    if (inSelection) {
      base.applyOptions({
        lineColor: GRAY_LINE,
        topColor: GRAY_FILL_TOP,
        bottomColor: FILL_BOTTOM,
      })
      sel.applyOptions({
        lineColor: selIsUp ? UP_LINE : DOWN_LINE,
        topColor: selIsUp ? UP_FILL_TOP : DOWN_FILL_TOP,
        bottomColor: FILL_BOTTOM,
      })
    } else {
      base.applyOptions({
        lineColor,
        topColor: fillTop,
        bottomColor: FILL_BOTTOM,
      })
    }
    chartRef.current?.applyOptions({
      timeScale: {
        timeVisible: range === '1D' || range === '1W',
        secondsVisible: false,
      },
    })
  }, [lineColor, fillTop, range, inSelection, selIsUp])

  // Populate / clear the colored selection overlay segment.
  useEffect(() => {
    const sel = selSeriesRef.current
    if (!sel) return
    if (!selection || !selLeft || !selRight) {
      sel.setData([])
      return
    }
    const startT = selLeft.time
    const endT = selRight.time
    const segment = points
      .filter((p) => p.t >= startT && p.t <= endT)
      .map((p) => ({
        time: toLocalChartTime(p.t),
        value: p.v,
      }))
    sel.setData(segment)
  }, [selection, selLeft, selRight, points])

  useEffect(() => {
    if (!seriesRef.current) return
    seriesRef.current.setData(
      points.map((p) => ({
        time: toLocalChartTime(p.t),
        value: p.v,
      })),
    )
    chartRef.current?.timeScale().fitContent()
  }, [points])

  if (!accountId) return null

  const hasData = points.length > 0

  return (
    <div className="px-4 py-4 border-b border-zinc-800">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-zinc-500">
            Portfolio value
            {inSelection ? (
              <span className="ml-2 normal-case tracking-normal text-zinc-400">
                {fmtHeaderDateTime(selection!.start.time)}
                {' → '}
                {fmtHeaderDateTime(selection!.end.time)}
              </span>
            ) : (
              hovered && displayTime != null && (
                <span className="ml-2 normal-case tracking-normal text-zinc-400">
                  {fmtHeaderDateTime(displayTime)}
                </span>
              )
            )}
          </div>
          <div className="font-mono text-2xl text-zinc-100 leading-tight">
            {hasData ? mask(fmtMoneyRaw(displayValue)) : isLoading ? '…' : '—'}
          </div>
          {hasData && (inSelection ? selStartValue > 0 : first > 0) && (
            <div
              className={`font-mono text-xs ${isUp ? 'text-emerald-400' : 'text-red-400'}`}
            >
              {isUp ? '+' : ''}
              {mask(fmtMoneyRaw(delta))} ({isUp ? '+' : ''}
              {deltaPct.toFixed(2)}%) · {inSelection ? 'selection' : range}
            </div>
          )}
          {showRealizedPL && !inSelection && (
            <div
              className={`font-mono text-xs mt-0.5 ${
                todayPL!.realizedPL >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}
              title="Realized P/L from trades closed today, as % of start-of-day portfolio"
            >
              {todayPL!.realizedPL >= 0 ? '+' : ''}
              {mask(fmtMoneyRaw(todayPL!.realizedPL))}
              {todayPL!.realizedPLPct != null && (
                <>
                  {' '}
                  ({todayPL!.realizedPLPct >= 0 ? '+' : ''}
                  {todayPL!.realizedPLPct.toFixed(2)}%)
                </>
              )}{' '}
              <span className="text-zinc-500">· realized today</span>
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              aria-pressed={r === range}
              className={`px-2 py-1 text-[11px] rounded font-mono border transition-colors ${
                r === range
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  : 'text-zinc-400 hover:text-zinc-100 border-transparent'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div
        ref={wrapRef}
        className={`relative h-[160px] select-none ${
          hasData ? (inSelection ? 'cursor-ew-resize' : 'cursor-crosshair') : ''
        }`}
      >
        <div ref={containerRef} className="absolute inset-0" />
        {selection && (
          <>
            <div
              className="absolute top-0 bottom-0 left-0 pointer-events-none bg-zinc-950/30"
              style={{
                width: Math.max(0, Math.min(selection.start.x, selection.end.x)),
              }}
            />
            <div
              className="absolute top-0 bottom-0 right-0 pointer-events-none bg-zinc-950/30"
              style={{
                left: Math.max(selection.start.x, selection.end.x),
              }}
            />
            <div
              className={`absolute top-0 bottom-0 pointer-events-none border-l border-r ${
                selIsUp ? 'border-emerald-500/40' : 'border-red-500/40'
              }`}
              style={{
                left: Math.min(selection.start.x, selection.end.x),
                width: Math.max(1, Math.abs(selection.end.x - selection.start.x)),
              }}
            />
          </>
        )}
        {isLoading && !hasData && (
          <div className="absolute inset-0 animate-pulse bg-zinc-900/30" />
        )}
        {!isLoading && !hasData && (
          <div className="absolute inset-0 grid place-items-center text-xs text-zinc-500">
            Recording portfolio history… check back soon.
          </div>
        )}
      </div>
      {tickLabels.length > 0 && (
        <div className="relative mt-1 h-4 font-mono text-[11px] text-zinc-500">
          {tickLabels.map((label, i) => {
            const pct = (i / (tickLabels.length - 1)) * 100
            const transform =
              i === 0
                ? 'translateX(0)'
                : i === tickLabels.length - 1
                  ? 'translateX(-100%)'
                  : 'translateX(-50%)'
            return (
              <span
                key={i}
                className="absolute top-0 whitespace-nowrap"
                style={{ left: `${pct}%`, transform }}
              >
                {label}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}
